import type { ReactNode } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "@mui/material/SvgIcon";

import HomeSmileIcon from "src/icons/untitled-ui/duocolor/home-smile";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import useUserDataProvider from "src/contexts/snug/userData-context";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AddHomeWorkOutlinedIcon from '@mui/icons-material/AddHomeWorkOutlined';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';

import { tokens } from "src/locales/tokens";
import { paths } from "src/paths";

export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
}

export interface Section {
  items: Item[];
  subheader?: string;
}

export const useSections = () => {
  const { t } = useTranslation();
  const { professionalGroup, isPaidTier } =
    useUserDataProvider();

  return useMemo(() => {
    const servicesItems = [
      ...(professionalGroup?.allow_analysis && isPaidTier
        ? [
            {
              title: `Analyze Documents`,
              path: paths.dashboard.clientRoles.analysis,
              icon: (
                <SvgIcon fontSize="small">
                  <InsightsOutlinedIcon />
                </SvgIcon>
              ),
            },
          ]
        : []),
      ...(professionalGroup?.allow_quitclaim_deed
        ? [
            {
              title: `Quitclaim Deeds`,
              path: paths.dashboard.quitclaimDeeds,
              icon: (
                <SvgIcon fontSize="small">
                  <AddHomeWorkOutlinedIcon />
                </SvgIcon>
              ),
            },
          ]
        : []),
    ].filter(Boolean) as Item[];

    return [
      {
        items: [
          {
            title: `Home`,
            path: paths.dashboard.index,
            icon: (
              <SvgIcon fontSize="small">
                <HomeSmileIcon />
              </SvgIcon>
            ),
          },
          ...(!professionalGroup?.onboarding_completed_at
            ? [
                {
                  title: `Complete Onboarding`,
                  path: paths.dashboard.upgrade,
                  icon: (
                    <SvgIcon fontSize="small">
                      <AutoAwesomeOutlinedIcon />
                    </SvgIcon>
                  ),
                },
              ]
            : []),
          // ...(isFirmAdmin && professionalGroup?.allow_referrals
          //   ? [
          //       {
          //         title: `Refer Friends`,
          //         path: paths.dashboard.referrals,
          //         icon: (
          //           <SvgIcon fontSize="small">
          //             <CardGiftcardOutlinedIcon />
          //           </SvgIcon>
          //         ),
          //       },
          //     ]
          //   : []),
          {
            title: t(tokens.nav.account),
            path: paths.dashboard.account,
            icon: (
              <SvgIcon fontSize="small">
                <ManageAccountsOutlinedIcon />
              </SvgIcon>
            ),
          },
        ],
      },
      {
        subheader: `Clients`,
        items: [
          {
            title: `Add Client`,
            path: paths.dashboard.clientRoles.create,
            icon: (
              <SvgIcon fontSize="small">
                <GroupAddOutlinedIcon />
              </SvgIcon>
            ),
          },
          {
            title: `Client List`,
            path: paths.dashboard.clientRoles.index,
            icon: (
              <SvgIcon fontSize="small">
                <GroupOutlinedIcon />
              </SvgIcon>
            ),
          },
          ...(professionalGroup?.allow_view_transactions
            ? [
                {
                  title: `Transactions`,
                  path: paths.dashboard.transactions,
                  icon: (
                    <SvgIcon fontSize="small">
                      <ReceiptLongOutlinedIcon />
                    </SvgIcon>
                  ),
                },
              ]
            : []),
        ].filter(Boolean) as Item[],
      },
      ...(servicesItems.length > 0
        ? [
            {
              subheader: `Services`,
              items: servicesItems,
            },
          ]
        : []),
      {
        subheader: `Resources`,
        items: [
          {
            title: `Pro Toolkit`,
            path: professionalGroup?.id === '03ceb0f6-9456-4a4f-a26f-01b090b44d68'
              ? paths.dashboard.protoolkit // TODO: Replace with specific path
              : paths.dashboard.toolkit,
            icon: (
              <SvgIcon fontSize="small">
                <BookmarkBorderOutlinedIcon />
              </SvgIcon>
            ),
          },
          {
            title: `Learning Center`,
            path: paths.dashboard.learning,
            icon: (
              <SvgIcon fontSize="small">
                <SchoolOutlinedIcon />
              </SvgIcon>
            ),
          },
          {
            title: `Tutorials`,
            path: professionalGroup?.id === '03ceb0f6-9456-4a4f-a26f-01b090b44d68'
              ? paths.dashboard.demo // TODO: Replace with specific path
              : paths.dashboard.tutorials,
            icon: (
              <SvgIcon fontSize="small">
                <OndemandVideoOutlinedIcon />
              </SvgIcon>
            ),
          },
          // ...(professionalGroup?.allow_scenarios
          //   ? [
          //       {
          //         title: `Scenarios`,
          //         path: paths.dashboard.scenarios,
          //         icon: (
          //           <SvgIcon fontSize="small">
          //             <AccountTreeOutlinedIcon />
          //           </SvgIcon>
          //         ),
          //       },
          //     ]
          //   : []),
          ...(professionalGroup?.allow_marketing
            ? [
                {
                  title: `Marketing`,
                  path: paths.dashboard.marketing,
                  icon: (
                    <SvgIcon fontSize="small">
                      <CampaignOutlinedIcon />
                    </SvgIcon>
                  ),
                },
              ]
            : []),
          {
            title: `Will vs. Trust Quiz`,
            path: paths.dashboard.quiz,
            icon: (
              <SvgIcon fontSize="small">
                <QuizOutlinedIcon />
              </SvgIcon>
            ),
          },
        ],
      },
    ];
  }, [
    t,
    isPaidTier,
    professionalGroup?.allow_analysis,
    professionalGroup?.onboarding_completed_at,
    professionalGroup?.allow_view_transactions,
    // professionalGroup?.allow_referrals,
    professionalGroup?.allow_marketing,
    professionalGroup?.allow_quitclaim_deed,
    // professionalGroup?.allow_scenarios,
    professionalGroup?.id
  ]);
};
